// Cabecalho do cardapio publico.
//
// Duas ESTRUTURAS, uma por canal, espelhando os dois prints de referencia que o
// dono mandou (mesmo app, web e celular):
//
//   COMPUTADOR — logo redonda a ESQUERDA, ao lado de um cartao que ocupa o
//   resto da largura. Logo e cartao com a mesma altura.
//
//   CELULAR — a logo NAO fica ao lado do cartao: ela sobrepoe o banner, e o
//   cartao ocupa a LARGURA INTEIRA. Foi o que resolveu o problema real que
//   vinha se arrastando — com a logo do lado sobravam ~210px de texto e nome e
//   endereco cortavam com reticencias. Entre o banner e o cartao entra uma
//   faixa so com as metricas (delivery, retirada, minimo), como na referencia.
//
// Sao duas arvores de JSX porque a diferenca e' de ESTRUTURA (o que fica dentro
// do cartao e o que fica fora), nao de estilo — resolver por classes
// responsivas viraria uma teia de `lg:` impossivel de mexer depois. Os pedacos
// (StoreIdentity, StoreChips, HorariosModal) sao os mesmos nos dois.

import { UtensilsCrossed } from "lucide-react";
import { useEffect, useState } from "react";

import HorariosModal from "./components/HorariosModal";
import StoreChips from "./components/StoreChips";
import StoreIdentity from "./components/StoreIdentity";
import { enderecoCompleto, enderecoCurto } from "./lib/endereco";
import { buildMapsUrl } from "./lib/socialLinks";

// Slogan que vem por padrao do sistema quando a loja nunca preencheu o campo.
// E' o slogan do NUCLEO, nao da loja — mostra-lo no cardapio do cliente seria
// anunciar o fornecedor na vitrine de quem contratou.
const SLOGAN_PADRAO_SISTEMA = "o centro da sua gestão";

const LOGO_MOBILE = 76;
const LOGO_DESKTOP = 156;
// No computador o cartao tem a MESMA altura da logo (pedido do dono). No
// celular a logo esta fora do cartao, entao nao ha altura a casar.
const PISO_CARTAO_DESKTOP = 156;
const LG_PX = 1024;

/** `true` a partir do breakpoint `lg`. Precisa ser JS, e nao classe
 *  responsiva, porque o numero vira `width`/`height` inline da logo. */
function useEhDesktop() {
  const calc = () => typeof window !== "undefined" && window.innerWidth >= LG_PX;
  const [ehDesktop, setEhDesktop] = useState(calc);
  useEffect(() => {
    const aplicar = () => setEhDesktop(calc());
    aplicar();
    window.addEventListener("resize", aplicar);
    return () => window.removeEventListener("resize", aplicar);
  }, []);
  return ehDesktop;
}

export default function CardapioHero(props) {
  const { companySettings, businessHours, t, darkMode } = props;
  const [horariosAbertos, setHorariosAbertos] = useState(false);
  const ehDesktop = useEhDesktop();

  const dados = {
    ...props,
    nome: companySettings?.fantasy_name || companySettings?.company_name || "Restaurante",
    slogan: sloganDaLoja(companySettings?.slogan),
    curto: enderecoCurto(companySettings),
    mapsUrl: buildMapsUrl(enderecoCompleto(companySettings)),
    abrirHorarios: () => setHorariosAbertos(true),
  };

  return (
    <>
      {ehDesktop
        ? <HeroDesktop {...dados} />
        : <HeroMobile {...dados} />}

      {horariosAbertos && (
        <HorariosModal
          businessHours={businessHours}
          t={t}
          darkMode={darkMode}
          onClose={() => setHorariosAbertos(false)}
        />
      )}
    </>
  );
}

/** Slogan a exibir — vazio quando nao ha um proprio da loja. */
function sloganDaLoja(bruto) {
  const texto = (bruto || "").trim();
  if (!texto || texto.toLowerCase() === SLOGAN_PADRAO_SISTEMA) return "";
  return texto;
}

function Logo({ tamanho, logoUrl, nome, onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Ver informações da loja"
      aria-label="Ver informações da loja"
      style={{ width: tamanho, height: tamanho }}
      className={`flex-shrink-0 rounded-full bg-white border-4 border-white shadow-xl overflow-hidden flex items-center justify-center cursor-pointer hover:scale-105 active:scale-95 transition-transform ${className}`}
    >
      {logoUrl ? (
        <img src={logoUrl} alt={nome} className="w-full h-full object-cover" />
      ) : (
        <UtensilsCrossed className="w-1/3 h-1/3 text-primary" />
      )}
    </button>
  );
}

function HeroDesktop(d) {
  return (
    <div className="flex items-stretch gap-5 px-4 -mt-16 relative z-10">
      <Logo tamanho={LOGO_DESKTOP} logoUrl={d.logoUrl} nome={d.nome}
            onClick={d.onOpenInfo} className="self-start" />
      <div
        style={{ minHeight: PISO_CARTAO_DESKTOP }}
        className={`flex-1 min-w-0 ${d.t.bgCard} rounded-2xl shadow-lg border ${d.t.border} px-5 py-4 flex flex-col justify-center gap-3`}
      >
        <StoreIdentity
          nome={d.nome} slogan={d.slogan} stats={d.stats} enderecoCurto={d.curto} mapsUrl={d.mapsUrl}
          t={d.t} onShare={d.onShare} onOpenReviews={d.onOpenReviews}
        />
        <Chips d={d} partes="todas" />
      </div>
    </div>
  );
}

function HeroMobile(d) {
  return (
    <div className="relative z-10">
      {/* Logo sobreposta ao banner, encostada na esquerda. `absolute` de
          proposito: assim ela nao rouba largura do cartao nem da faixa. */}
      <Logo tamanho={LOGO_MOBILE} logoUrl={d.logoUrl} nome={d.nome}
            onClick={d.onOpenInfo} className="absolute left-3 -top-9 z-20" />

      {/* Faixa de metricas entre o banner e o cartao. `pl` abre espaco pra logo
          que passa por cima dela. */}
      <div className="pl-[92px] pr-3 pt-1.5 pb-2 min-h-[32px] flex items-center">
        <Chips d={d} partes="metricas" />
      </div>

      <div className={`mx-3 ${d.t.bgCard} rounded-2xl shadow-lg border ${d.t.border} px-3 py-3 flex flex-col gap-2`}>
        <StoreIdentity
          nome={d.nome} slogan={d.slogan} stats={d.stats} enderecoCurto={d.curto} mapsUrl={d.mapsUrl}
          t={d.t} onShare={d.onShare} onOpenReviews={d.onOpenReviews}
        />
        <Chips d={d} partes="status" />
      </div>
    </div>
  );
}

function Chips({ d, partes }) {
  return (
    <StoreChips
      partes={partes}
      isOpen={d.isOpen}
      closingTime={d.closingTime}
      manuallyClosed={d.manuallyClosed}
      closeReason={d.closeReason}
      nextOpenTime={d.nextOpenTime}
      schedulingActive={d.schedulingActive}
      stats={d.stats}
      darkMode={d.darkMode}
      businessHours={d.businessHours}
      onOpenHorarios={d.abrirHorarios}
      onOpenInfo={d.onOpenInfo}
    />
  );
}
